/**
 * Route geometry and turn instructions. Pure: no DOM, no network, no provider
 * specifics — the Worker normalises whichever routing engine answered into this
 * shape, and the browser only ever sees this shape.
 */
import { haversine, type Point } from "./geo.js";

export type ManeuverKind =
  | "depart" | "arrive" | "turn" | "keep" | "straight" | "roundabout" | "exit_roundabout" | "uturn" | "merge" | "fork";
export type Modifier =
  | "left" | "right" | "slight_left" | "slight_right" | "sharp_left" | "sharp_right" | "straight" | null;

export type Maneuver = {
  kind: ManeuverKind;
  modifier: Modifier;
  /** Roundabout exit number, when known. */
  exit: number | null;
  /** Road name after the manoeuvre, "" when unnamed. */
  name: string;
  /** Where the manoeuvre happens. */
  lat: number;
  lon: number;
  /** Length of the step that FOLLOWS this manoeuvre. */
  distanceM: number;
  durationS: number;
};

export type Route = {
  distanceM: number;
  durationS: number;
  /** [lat, lon] vertices, start to end. */
  geometry: readonly (readonly [number, number])[];
  steps: readonly Maneuver[];
  /** Which engine answered; shown in attribution only. "fanar" = our own graph, on the phone. */
  provider: "ors" | "osrm" | "fanar";
};

/* ---------------- Albanian instructions ---------------- */

const SIDE: Record<Exclude<Modifier, null>, string> = {
  left: "majtas", right: "djathtas",
  slight_left: "lehtë majtas", slight_right: "lehtë djathtas",
  sharp_left: "fort majtas", sharp_right: "fort djathtas",
  straight: "drejt",
};

const ORDINAL = ["", "e parë", "e dytë", "e tretë", "e katërt", "e pestë", "e gjashtë", "e shtatë", "e tetë", "e nëntë"];

/** The sentence a driver reads on the card. Never a number the driver must parse
 *  at a glance beyond the distance, which is rendered separately. */
export function instructionText(m: Maneuver): string {
  const onto = m.name !== "" ? ` në ${m.name}` : "";
  switch (m.kind) {
    case "depart": return m.name !== "" ? `Nisu${onto}` : "Nisu";
    case "arrive": return "Arrite në destinacion";
    case "uturn": return `Kthehu mbrapa${onto}`;
    case "roundabout": {
      const exit = m.exit !== null && m.exit > 0 && m.exit < ORDINAL.length ? `, dalja ${ORDINAL[m.exit]}` : "";
      return `Hyr në rrethrrotullim${exit}${onto}`;
    }
    case "exit_roundabout": return `Dil nga rrethrrotullimi${onto}`;
    case "merge": return `Bashkohu me trafikun${onto}`;
    case "fork":
    case "keep": {
      const side = m.modifier === null || m.modifier === "straight" ? "drejt" : SIDE[m.modifier].replace(/^(lehtë|fort) /, "");
      return `Mbaje ${side}${onto}`;
    }
    case "straight": return `Vazhdo drejt${onto}`;
    case "turn": {
      if (m.modifier === null || m.modifier === "straight") return `Vazhdo drejt${onto}`;
      return `Kthehu ${SIDE[m.modifier]}${onto}`;
    }
  }
}

/** "300 m", "1,2 km", "12 km" — Albanian decimal comma, no more precision than a
 *  driver can use. */
export function distanceText(metres: number): string {
  if (metres < 1000) return `${Math.max(10, Math.round(metres / 10) * 10)} m`;
  if (metres < 10_000) return `${(metres / 1000).toFixed(1).replace(".", ",")} km`;
  return `${Math.round(metres / 1000)} km`;
}

export function durationText(seconds: number): string {
  const min = Math.round(seconds / 60);
  if (min < 60) return `${Math.max(1, min)} min`;
  const h = Math.floor(min / 60);
  const rest = min % 60;
  return rest === 0 ? `${h} orë` : `${h} orë ${rest} min`;
}

/* ---------------- geometry ---------------- */

export type RouteIndex = {
  route: Route;
  /** Cumulative metres at each vertex. */
  cum: number[];
  /** Metres along the route at each manoeuvre. */
  stepAlong: number[];
};

const R = 6371008.8;

/** Local flat projection around a latitude: good to well under 1 m over the
 *  hundreds of metres a projection ever spans. */
function toXY(p: Point, lat0: number): { x: number; y: number } {
  const k = (Math.PI / 180) * R;
  return { x: p.lon * k * Math.cos((lat0 * Math.PI) / 180), y: p.lat * k };
}

export function indexRoute(route: Route): RouteIndex {
  const cum: number[] = [0];
  for (let i = 1; i < route.geometry.length; i++) {
    const a = route.geometry[i - 1] as readonly [number, number];
    const b = route.geometry[i] as readonly [number, number];
    cum.push((cum[i - 1] as number) + haversine({ lat: a[0], lon: a[1] }, { lat: b[0], lon: b[1] }));
  }
  const idx: RouteIndex = { route, cum, stepAlong: [] };
  idx.stepAlong = route.steps.map((s) => projectOnRoute(idx, { lat: s.lat, lon: s.lon }).alongM);
  return idx;
}

export type Projection = { segment: number; alongM: number; offM: number };

/** Nearest point on the polyline: which segment, how far along the route, how
 *  far off it. O(n) over vertices — routes in Albania are a few hundred points. */
export function projectOnRoute(idx: RouteIndex, p: Point): Projection {
  const g = idx.route.geometry;
  if (g.length === 0) return { segment: 0, alongM: 0, offM: Infinity };
  if (g.length === 1) {
    const only = g[0] as readonly [number, number];
    return { segment: 0, alongM: 0, offM: haversine(p, { lat: only[0], lon: only[1] }) };
  }
  const lat0 = p.lat;
  const P = toXY(p, lat0);
  let best: Projection = { segment: 0, alongM: 0, offM: Infinity };
  for (let i = 0; i < g.length - 1; i++) {
    const a = g[i] as readonly [number, number];
    const b = g[i + 1] as readonly [number, number];
    const A = toXY({ lat: a[0], lon: a[1] }, lat0);
    const B = toXY({ lat: b[0], lon: b[1] }, lat0);
    const dx = B.x - A.x, dy = B.y - A.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((P.x - A.x) * dx + (P.y - A.y) * dy) / len2));
    const qx = A.x + t * dx, qy = A.y + t * dy;
    const off = Math.hypot(P.x - qx, P.y - qy);
    if (off < best.offM) {
      const segLen = (idx.cum[i + 1] as number) - (idx.cum[i] as number);
      best = { segment: i, alongM: (idx.cum[i] as number) + t * segLen, offM: off };
    }
  }
  return best;
}

/** Off the route for real, not GPS noise: 60 m is two carriageways plus error. */
export const OFF_ROUTE_M = 60;
/** A hazard "on the route" sits within this of the line. */
export const ON_ROUTE_M = 40;

/** The manoeuvre coming up and the metres left to it, given progress along the
 *  route. The first `depart` step is skipped once the driver has moved. */
export function nextManeuver(idx: RouteIndex, alongM: number): { step: Maneuver; distanceM: number; index: number } | null {
  for (let i = 0; i < idx.route.steps.length; i++) {
    const step = idx.route.steps[i] as Maneuver;
    const at = idx.stepAlong[i] as number;
    if (step.kind === "depart") continue;
    // A manoeuvre stays "next" until the driver is 15 m past it.
    if (at + 15 >= alongM) return { step, distanceM: Math.max(0, at - alongM), index: i };
  }
  return null;
}

/** Distance along the route from the driver to a hazard that sits on it, or
 *  null when the hazard is off the route or already behind. Replaces the
 *  bearing-cone test while navigating: a hazard around the next bend is ahead
 *  even though the compass says otherwise. */
export function routeDistanceTo(idx: RouteIndex, myAlongM: number, hazard: Point): number | null {
  const p = projectOnRoute(idx, hazard);
  if (p.offM > ON_ROUTE_M) return null;
  const d = p.alongM - myAlongM;
  return d < 0 ? null : d;
}

/** The point `alongM` metres into the route and the bearing of that segment.
 *  Used by the drive simulator to follow a route. */
export function pointAlong(idx: RouteIndex, alongM: number): { lat: number; lon: number; bearing: number } {
  const g = idx.route.geometry;
  const last = g[g.length - 1] as readonly [number, number];
  if (g.length < 2) return { lat: last[0], lon: last[1], bearing: 0 };
  const total = idx.cum[idx.cum.length - 1] as number;
  const d = Math.max(0, Math.min(total, alongM));
  let i = 0;
  while (i < idx.cum.length - 2 && (idx.cum[i + 1] as number) < d) i++;
  const a = g[i] as readonly [number, number];
  const b = g[i + 1] as readonly [number, number];
  const segLen = (idx.cum[i + 1] as number) - (idx.cum[i] as number);
  const t = segLen === 0 ? 0 : (d - (idx.cum[i] as number)) / segLen;
  const lat = a[0] + (b[0] - a[0]) * t;
  const lon = a[1] + (b[1] - a[1]) * t;
  const y = Math.sin(((b[1] - a[1]) * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180);
  const x = Math.cos((a[0] * Math.PI) / 180) * Math.sin((b[0] * Math.PI) / 180) -
    Math.sin((a[0] * Math.PI) / 180) * Math.cos((b[0] * Math.PI) / 180) * Math.cos(((b[1] - a[1]) * Math.PI) / 180);
  const bearing = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  return { lat, lon, bearing };
}
